import type { FieldComponentProps } from '../fieldRegistry';
import { EmployeeSelector } from '../EmployeeSelector';

/**
 * EmployeeField adapts the shared EmployeeSelector to the field-registry
 * interface, mirroring AddressField (issue #1217).
 *
 * `field.excludeEntityId` is forwarded as `excludeEmployeeId`, which is how the
 * direct-head field hides the employee being edited.
 *
 * @template T - Must extend string since employee fields store employee IDs
 */
export function EmployeeField<T extends string = string>({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: FieldComponentProps<T>) {
  const employeeValue = typeof value === 'string' ? value : '';

  const handleChange = (employeeId: string | undefined) => {
    // "" is the explicit clear the backend expects; it must not become
    // `undefined`, which the update transform would drop.
    onChange((employeeId || '') as T);
    onBlur();
  };

  return (
    <EmployeeSelector
      value={employeeValue}
      onChange={handleChange}
      placeholder={placeholder ?? field.placeholder}
      disabled={disabled ?? field.disabled}
      ariaLabel={field.label}
      // BaseEditableForm supplies both: `id` makes the field's <label htmlFor>
      // resolve to the trigger, and aria-describedby links the error and
      // description text. Dropping them breaks both associations.
      id={id}
      ariaDescribedBy={ariaDescribedBy}
      ariaInvalid={ariaInvalid}
      ariaRequired={field.required || undefined}
      excludeEmployeeId={field.excludeEntityId}
    />
  );
}

export default EmployeeField;
